// frontend/src/components/EsignStatusIndicator.js
//
// ESIGN-UX-REDESIGN-001 — Story 2 (status indicator), Surface 1: customer
// profile / account page (dark-theme).
//
// Spec sources (locked):
//   docs/design/ESIGN-UX-REDESIGN-001-status-indicator-design.md (Zuri)
//     §3 — customer profile placement + 4 visual states (signed / partial /
//          not-signed / revoked) + loading + error
//     §3.4 — accessibility
//   docs/architecture/ESIGN-UX-REDESIGN-001-tech.md (Marcus)
//     §Story 2 — esignStatus block shape (consumed via /api/profile)
//     §Story 2 — privacy guardrail (NO PDF, NO signature image, NO IP)
//     §Story 2 — graceful no-op if esignStatus is missing from response
//
// Privacy posture (DO NOT REGRESS):
//   This component renders status flags + timestamps ONLY. It MUST NOT add:
//     - signed PDF preview / inline viewer
//     - signature image render
//     - IP address from SignWell envelope metadata
//     - SignWell envelope ID exposure
//   Any future polish that drifts toward "view signed PDF" affordances must
//   route through a separate higher-permission flow per Marcus + Zuri.
//
// Production-safety:
//   - Renders nothing if `esignStatus` block is missing from props (graceful
//     fallback while backend is mid-deploy of the additive contract).
//   - Pure-presentational: no API calls, no side effects beyond navigation.

import React from "react";
import { useNavigate } from "react-router-dom";
import { deriveOverallStatus, WAIVER_GATE_PATH } from "../services/waiverApi";
import "./EsignStatusIndicator.css";

/**
 * Format a UTC ISO timestamp as "Apr 12, 2026 at 2:32pm MST" for display in
 * the customer's MST-anchored UI. Mirrors WaiverReturn.js formatSignedAt
 * style so customers see consistent timestamp formatting across surfaces.
 */
function formatSignedAt(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  // Phoenix never observes DST so "MST" is always correct.
  const dateOpts = {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "America/Phoenix",
  };
  const datePart = d.toLocaleDateString("en-US", dateOpts);
  return `Signed ${datePart} MST`;
}

/**
 * Pick the most recent signed timestamp for the "signed [date]" label.
 */
function latestSignedAt(esignStatus) {
  if (!esignStatus) return null;
  const a = esignStatus.consentSignedAt
    ? new Date(esignStatus.consentSignedAt).getTime()
    : 0;
  const b = esignStatus.releaseSignedAt
    ? new Date(esignStatus.releaseSignedAt).getTime()
    : 0;
  if (!a && !b) return null;
  return a > b ? esignStatus.consentSignedAt : esignStatus.releaseSignedAt;
}

/**
 * Customer-facing ESIGN status indicator. Renders one of four visual
 * states based on the `esignStatus` block surfaced on /api/profile.
 *
 * @param {object} props
 * @param {object} [props.esignStatus] — see EsignStatus type in tech doc
 *   Data Models section. Omitted/null/undefined renders nothing (graceful
 *   fallback when the backend has not yet deployed the additive contract).
 * @param {boolean} [props.loading=false] — render skeleton placeholder.
 */
const EsignStatusIndicator = ({ esignStatus, loading = false }) => {
  const navigate = useNavigate();

  if (loading) {
    return (
      <section
        className="esign-status-indicator esign-status-indicator--loading"
        aria-label="ESIGN compliance status"
        aria-busy="true"
        role="status"
      >
        <div className="esign-status-indicator__skeleton esign-status-indicator__skeleton--line" />
        <div className="esign-status-indicator__skeleton esign-status-indicator__skeleton--subline" />
      </section>
    );
  }

  // Graceful fallback: backend not yet emitting esignStatus block on
  // /api/profile (e.g., FE deployed before BE additive contract). Render
  // nothing — surrounding profile page renders normally.
  if (!esignStatus) {
    return null;
  }

  const overall = deriveOverallStatus(esignStatus);
  const handleSignNow = () => navigate(WAIVER_GATE_PATH);

  // ---- Signed (default for compliant customers) ---------------------------
  if (overall === "signed") {
    const ts = latestSignedAt(esignStatus);
    return (
      <section
        className="esign-status-indicator esign-status-indicator--signed"
        aria-label="ESIGN compliance status"
      >
        <div className="esign-status-indicator__row">
          <span
            className="esign-status-indicator__icon esign-status-indicator__icon--signed"
            aria-hidden="true"
          >
            {/* Check icon — colors.success in CSS */}
            <svg viewBox="0 0 20 20" width="20" height="20" fill="none">
              <circle cx="10" cy="10" r="9" fill="currentColor" />
              <path
                d="M6 10l3 3 5-6"
                stroke="#1a1a1a"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
              />
            </svg>
          </span>
          <span className="esign-status-indicator__primary">
            Consent + release signed
          </span>
        </div>
        {ts && (
          <p className="esign-status-indicator__subtitle">
            {formatSignedAt(ts)}
          </p>
        )}
      </section>
    );
  }

  // ---- Revoked (admin action — rare) --------------------------------------
  if (overall === "revoked") {
    return (
      <section
        className="esign-status-indicator esign-status-indicator--revoked"
        aria-label="ESIGN compliance status"
      >
        <div className="esign-status-indicator__row">
          <span
            className="esign-status-indicator__icon esign-status-indicator__icon--revoked"
            aria-hidden="true"
          >
            {/* Error / x-circle filled */}
            <svg viewBox="0 0 20 20" width="20" height="20" fill="none">
              <circle cx="10" cy="10" r="9" fill="currentColor" />
              <path
                d="M7 7l6 6M13 7l-6 6"
                stroke="#ffffff"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </span>
          <span className="esign-status-indicator__primary">
            Consent or release revoked
          </span>
        </div>
        <p className="esign-status-indicator__subtitle">
          One or more forms have been revoked. Please contact support or
          re-sign.
        </p>
        <button
          type="button"
          className="esign-status-indicator__cta"
          onClick={handleSignNow}
          aria-label="Re-sign your consent and release form now"
        >
          Re-sign now
        </button>
      </section>
    );
  }

  // ---- Partial / Not-signed (CTA-bearing) ---------------------------------
  // The two states differ only by primary copy + sub-copy + CTA label.
  const consent = Boolean(esignStatus.consentSigned);
  const release = Boolean(esignStatus.releaseSigned);

  let primary;
  let subtitle;
  let ctaLabel;
  if (overall === "partial") {
    if (consent && !release) {
      primary = "Consent signed; release pending";
      subtitle = "Sign the release form to complete setup.";
      ctaLabel = "Sign release";
    } else {
      primary = "Release signed; consent pending";
      subtitle = "Sign the consent form to complete setup.";
      ctaLabel = "Sign consent";
    }
  } else {
    // not-signed
    primary = "Consent + release not yet signed";
    subtitle = "Sign now to complete your account setup.";
    ctaLabel = "Sign now";
  }

  return (
    <section
      className="esign-status-indicator esign-status-indicator--pending"
      aria-label="ESIGN compliance status"
    >
      <div className="esign-status-indicator__row">
        <span
          className="esign-status-indicator__icon esign-status-indicator__icon--warn"
          aria-hidden="true"
        >
          {/* Warn-triangle filled — colors.gold in CSS */}
          <svg viewBox="0 0 20 20" width="20" height="20" fill="none">
            <path
              d="M10 2l9 16H1L10 2z"
              fill="currentColor"
              stroke="currentColor"
              strokeWidth="1"
              strokeLinejoin="round"
            />
            <path
              d="M10 7v5"
              stroke="#1a1a1a"
              strokeWidth="2"
              strokeLinecap="round"
            />
            <circle cx="10" cy="15" r="1" fill="#1a1a1a" />
          </svg>
        </span>
        <span className="esign-status-indicator__primary">{primary}</span>
      </div>
      <p className="esign-status-indicator__subtitle">{subtitle}</p>
      <button
        type="button"
        className="esign-status-indicator__cta"
        onClick={handleSignNow}
        aria-label={`${ctaLabel} — your consent and release form`}
      >
        {ctaLabel}
      </button>
    </section>
  );
};

export default EsignStatusIndicator;
